import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Select, { components } from 'react-select';

const DropdownIcon = () => (
  <svg className="w-4 h-4 text-gray-500 fill-none stroke-current" viewBox="0 0 24 24" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 9l-7 7-7-7" />
  </svg>
);

const CustomDropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <DropdownIcon />
    </components.DropdownIndicator>
  );
};

const HeroSection = () => {
  const navigate = useNavigate();
  const [lookingFor, setLookingFor] = useState('Bride (Female)');
  const [ageFrom, setAgeFrom] = useState('21');
  const [ageTo, setAgeTo] = useState('35');
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [districtOptions, setDistrictOptions] = useState([{ value: '', label: 'All Districts' }]);

  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Dynamically fetch Tamil Nadu districts and cities using async import
  useEffect(() => {
    import('country-state-city')
      .then(({ City }) => {
        const cities = City.getCitiesOfState('IN', 'TN').map((c) => ({
          value: c.name,
          label: c.name
        }));
        setDistrictOptions([{ value: '', label: 'All Districts' }, ...cities]);
      })
      .catch((err) => console.error('Failed to load district options:', err));
  }, []);

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: '#f9fafb',
      borderColor: state.isFocused ? '#8a216b' : '#e5e7eb',
      borderRadius: 'var(--bs-border-radius-lg, 0.5rem)',
      height: '46px',
      minHeight: '46px',
      boxShadow: state.isFocused ? '0 0 0 1px #8a216b' : 'none',
      '&:hover': {
        borderColor: '#8a216b'
      },
      cursor: 'pointer',
      fontSize: '0.875rem'
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 18px',
      height: '44px'
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      color: '#1f2937'
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6b7280',
      fontSize: '0.875rem',
      margin: 0
    }),
    singleValue: (base) => ({
      ...base,
      color: '#1f2937',
      fontSize: '0.875rem',
      margin: 0
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: '44px'
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: '6px 14px 6px 4px',
      color: '#6b7280'
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: '6px 4px',
      color: '#9ca3af'
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '1.25rem',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      zIndex: 50,
      overflow: 'hidden',
      marginTop: '6px'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#8a216b' : state.isFocused ? '#f3f4f6' : 'white',
      color: state.isSelected ? 'white' : '#1f2937',
      cursor: 'pointer',
      fontSize: '0.875rem',
      padding: '10px 16px'
    })
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    const filterState = {};

    if (lookingFor === 'Bride (Female)') {
      filterState.gender = 'Female';
    } else if (lookingFor === 'Groom (Male)') {
      filterState.gender = 'Male';
    }

    if (ageFrom) filterState.ageFrom = ageFrom;
    if (ageTo) filterState.ageTo = ageTo;
    if (selectedDistrict && selectedDistrict.value) {
      filterState.district = selectedDistrict.value;
    }

    try {
      sessionStorage.setItem('matrimony_search_filters', JSON.stringify(filterState));
    } catch (err) {
      console.error('Failed to save search filters:', err);
    }

    navigate('/matrimony/search', { state: filterState });
  };

  const heroBg = isMobile ? '/assets/images/mb_bg.png' : '/images/Frame 2147239532.webp';

  return (
    <section
      className="hero-section relative w-full bg-cover bg-center bg-no-repeat pt-6 sm:pt-14 md:pt-18 lg:pt-20 pb-10 sm:pb-16 md:pb-24 lg:pb-40 overflow-hidden"
      style={{
        backgroundImage: `url('${heroBg}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="w-full max-w-[1360px] mx-auto px-3.5 sm:px-6 relative z-10">
        <div className="flex flex-col items-center justify-center text-center">
          {/* Badge */}
          <div className="mb-3 sm:mb-6 lg:mb-8">
            <div className="inline-block bg-white px-3.5 sm:px-6 md:px-8 py-1.5 sm:py-2.5 rounded-full font-bold text-[10px] sm:text-xs md:text-sm lg:text-base uppercase tracking-wider shadow-md">
              <span className="bg-gradient-to-r from-[#8a216b] to-[#f03f16] bg-clip-text text-transparent fw-semibold">
                ANUPPAGOUNDAR TAMIL MATRIMONY
              </span>
            </div>
          </div>

          {/* Main Heading */}
          <h1 className="text-white text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.2] mb-3 sm:mb-5 max-w-4xl tracking-tight">
            Find Your Perfect Life Partner
          </h1>

          {/* Description */}
          <p className="text-white/90 text-xs sm:text-base md:text-lg lg:text-xl leading-relaxed mb-4 sm:mb-8 max-w-2xl px-2 sm:px-0">
            Connecting verified Appuppa Gounder brides and grooms through trusted, secure, and meaningful matchmaking.
          </p>

          {/* Feature Badges */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4 w-full sm:w-auto mb-4 sm:mb-8 lg:mb-10 px-1 sm:px-0">
            <div className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-6 md:px-8 py-1.5 sm:py-3 bg-white/10 backdrop-blur-md border border-white/30 rounded-xl text-white text-[10px] sm:text-xs md:text-sm font-semibold uppercase">
              <img src="/images/vector_1.png" alt="Lock" className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 object-contain flex-shrink-0" />
              <span>Privacy Controlled</span>
            </div>
            <div className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-6 md:px-8 py-1.5 sm:py-3 bg-white/10 backdrop-blur-md border border-white/30 rounded-xl text-white text-[10px] sm:text-xs md:text-sm font-semibold uppercase">
              <img src="/images/mingcute_love-fill.png" alt="Heart" className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 object-contain flex-shrink-0" />
              <span>Horoscope Porutham</span>
            </div>
            <div className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-6 md:px-8 py-1.5 sm:py-3 bg-white/10 backdrop-blur-md border border-white/30 rounded-xl text-white text-[10px] sm:text-xs md:text-sm font-semibold uppercase">
              <img src="/images/solar_star-bold-duotone.png" alt="Star" className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 object-contain flex-shrink-0" />
              <span>Gothram Transparency</span>
            </div>
          </div>

          {/* Search Card Filter Box */}
          <div className="w-full max-w-[1160px] bg-white rounded-2xl shadow-2xl p-3.5 sm:p-5 md:p-6 border-t-4 border-[#8a216b] my-2 sm:my-4 text-left">
            <form onSubmit={handleSearch} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 items-end">

              {/* FIELD 1: I'M LOOKING FOR */}
              <div>
                <label className="block text-[10px] sm:text-xs font-bold uppercase text-gray-700 mb-1 px-1 tracking-wider">
                  I&apos;M LOOKING FOR
                </label>
                <div className="relative">
                  <select
                    value={lookingFor}
                    onChange={(e) => setLookingFor(e.target.value)}
                    className="w-full h-[42px] sm:h-[46px] bg-[#f9fafb] border border-[#e5e7eb] rounded-3 pl-3 sm:pl-4 pr-10 text-xs sm:text-sm text-gray-800 focus:outline-none focus:border-[#8a216b] focus:ring-1 focus:ring-[#8a216b] cursor-pointer appearance-none font-medium"
                  >
                    <option value="Bride (Female)">Bride (Female)</option>
                    <option value="Groom (Male)">Groom (Male)</option>
                    <option value="Bride or Groom">Bride or Groom</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 sm:pr-4 pointer-events-none">
                    <DropdownIcon />
                  </div>
                </div>
              </div>

              {/* FIELD 2: AGE RANGE (COMBINED FROM & TO) */}
              <div>
                <label className="block text-[10px] sm:text-xs font-bold uppercase text-gray-700 mb-1 px-1 tracking-wider">
                  AGE RANGE
                </label>
                <div className="flex items-center h-[42px] sm:h-[46px] bg-[#f9fafb] border border-[#e5e7eb] rounded-3 pl-3 sm:pl-4 pr-2 sm:pr-3 focus-within:border-[#8a216b] focus-within:ring-1 focus-within:ring-[#8a216b]">
                  <select
                    value={ageFrom}
                    onChange={(e) => setAgeFrom(e.target.value)}
                    className="w-full bg-transparent text-xs sm:text-sm text-gray-800 focus:outline-none cursor-pointer py-1 px-0.5 appearance-none text-center font-medium"
                  >
                    {Array.from({ length: 43 }, (_, i) => i + 18).map((age) => (
                      <option key={age} value={age}>{age} Yrs</option>
                    ))}
                  </select>
                  <span className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase px-1 flex-shrink-0">TO</span>
                  <select
                    value={ageTo}
                    onChange={(e) => setAgeTo(e.target.value)}
                    className="w-full bg-transparent text-xs sm:text-sm text-gray-800 focus:outline-none cursor-pointer py-1 px-0.5 appearance-none text-center font-medium"
                  >
                    {Array.from({ length: 43 }, (_, i) => i + 20).map((age) => (
                      <option key={age} value={age}>{age} Yrs</option>
                    ))}
                  </select>
                  <div className="flex items-center pl-1 pointer-events-none flex-shrink-0">
                    <DropdownIcon />
                  </div>
                </div>
              </div>

              {/* FIELD 3: LOCATION (DISTRICT) - DYNAMIC REACT SELECT + COUNTRY-STATE-CITY */}
              <div>
                <label className="block text-[10px] sm:text-xs font-bold uppercase text-gray-700 mb-1 px-1 tracking-wider">
                  LOCATION (DISTRICT)
                </label>
                <Select
                  options={districtOptions}
                  value={selectedDistrict}
                  onChange={(val) => setSelectedDistrict(val)}
                  placeholder="Select or type district..."
                  isClearable
                  isSearchable
                  menuPlacement="bottom"
                  className="rounded-3"
                  classNamePrefix="react-select"
                  styles={customSelectStyles}
                  components={{ DropdownIndicator: CustomDropdownIndicator }}
                />
              </div>

              {/* SEARCH BUTTON */}
              <div>
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#8a216b] to-[#f03f16] text-white h-[42px] sm:h-[46px] px-5 sm:px-6 rounded-full font-bold text-sm sm:text-base md:text-lg flex items-center justify-center gap-2 shadow-lg hover:opacity-95 active:scale-[0.98] transition-all cursor-pointer"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 fill-current flex-shrink-0" viewBox="0 0 24 24">
                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                  </svg>
                  <span>Search</span>
                </button>
              </div>

            </form>
          </div>

          {/* Badge Bottom BELOW Search Card */}
          <div className="flex flex-col items-center gap-3 sm:gap-6 mt-4 sm:mt-6 mb-4 sm:mb-6">
            <p className="text-white text-[10px] sm:text-xs md:text-base font-semibold uppercase tracking-[2px] sm:tracking-[5px]">
              <span className="font-semibold text-black">100% Gothram verified matching </span>
              <span className="bg-gradient-to-r from-[#8a216b] to-[#f03f16] bg-clip-text text-transparent">✦</span>
            </p>
            <img src="/images/img_vecteezy_weddin.png" alt="Wedding Rings" className="w-16 sm:w-24 md:w-[106px] h-auto object-contain" />
          </div>
        </div>
      </div>

      {/* Trust Indicators Bar */}
      <div className="relative lg:absolute lg:bottom-0 lg:left-0 lg:right-0 border-t border-b border-text-primary/30 bg-white/80 backdrop-blur-sm pt-4 sm:pt-8 md:pt-10 lg:pt-12 pb-4 sm:pb-7 lg:pb-8 mt-4 lg:mt-0">
        <div className="w-full max-w-[1360px] mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 sm:flex items-center justify-center gap-3 sm:gap-8 md:gap-12 lg:gap-16">
            <TrustItem
              icon="/images/img_mdi_verified_user.png"
              text="100% ID Verified"
            />
            <TrustItem
              icon="/images/img_mdi_heart.png"
              text="2500+ Happy Couples"
            />
            <TrustItem
              icon="/images/img_mingcute_group_3_fill.png"
              text="15,000+ Verified Profiles"
            />
            <TrustItem
              icon="/images/img_mdi_eye_check.png"
              text="Zero Fake profiles"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const TrustItem = ({ icon, text }) => (
  <div className="flex items-center gap-2.5 sm:gap-3">
    <img src={icon} alt={text} className="w-7 sm:w-8 md:w-9 h-auto object-contain" />
    <span className="text-xs sm:text-sm md:text-base font-medium text-text-primary">
      {text}
    </span>
  </div>
);

export default HeroSection;
